import isUndefined from 'lodash/isUndefined';
import noop from 'lodash/noop';

import * as Constants from '../constants';
import { getInDomain } from '../domains/externalUtils';

const UseCaseActions = Constants.UseCases.ActionTypes;

export function autoLogin() {
  return {
    type: UseCaseActions.AUTO_LOGIN,
  };
}

export function changeInteractiveRadioMode({
  stationId,
  modeId,
  modeIndex,
  currentModeId,
  modePandoraId,
}) {
  return (dispatch, getState) => {
    const currentTrackToken = getInDomain('playback.utils.getCurrentTrackToken')(getState());
    const currentItemIndex = getInDomain(
      'playback.selectors.getCurrentItemIndex',
      null,
    )(getState());
    const track = getInDomain('playback.utils.getCurrentTrackFromActivePlaylist')(getState());
    const musicId = track.get('musicId');
    dispatch({
      type: UseCaseActions.CHANGE_INTERACTIVE_RADIO_MODE,
      data: {
        stationId,
        modeId,
        modeIndex,
        currentModeId,
        currentTrackToken,
        musicId,
        currentItemIndex,
        modePandoraId,
      },
    });
  };
}

export const closeSearch = (searchType = 'mainSearch') => ({
  type: UseCaseActions.CLOSE_SEARCH,
  data: {
    searchType,
  },
});

export const communityLogout = () => ({
  type: UseCaseActions.COMMUNITY_LOGOUT,
  data: {
    eventData: {
      action: Constants.Community.EventAction.COMMUNITY_LOGOUT,
      view_mode: Constants.Community.ViewMode.COMMUNITY_SSO_NAVIGATE,
      page_view: Constants.Community.PageView.COMMUNITY_REDIRECT,
    },
  },
});

export const filterSearch = (filterOption = 'all') => ({
  type: UseCaseActions.FILTER_SEARCH,
  data: {
    filterOption,
  },
});

export function handlePremiumAccess() {
  return {
    type: UseCaseActions.HANDLE_PREMIUM_ACCESS,
  };
}

/**
 * Action creator for starting the premium access coachmark & video display.
 *
 * @param {string} trigger - currently there's only one option for trigger on web, as defaulted.
 *                           when we start serving video plus, will also have to handle 'noavails'
 * @param {string} context - see domains/ads/rewards/externals.js for valid possibilities
 *                           defaulted to backstage because it's the most common case
 * @param {string} targetId - the pandoraId of the target
 *                            must be passed by caller for target to play on PA start
 * @param {string} targetTrackId - optional pandoraId of a specific track w/i a target source
 * @returns {{type: string, data: {trigger: string, context: string, target, targetId: *}}}
 */
export function initiatePremiumAccess({
  trigger = Constants.Ads.Rewards.TriggerTypes.AVAILS,
  context = Constants.Ads.Rewards.ContextTypes.BACKSTAGE,
  targetId,
  targetTrackId,
}) {
  return {
    type: UseCaseActions.INITIATE_PREMIUM_ACCESS,
    data: {
      trigger,
      context,
      targetId,
      targetTrackId,
    },
  };
}

export function linkClicked(event) {
  return dispatch => {
    dispatch({
      type: UseCaseActions.LINK_CLICKED,
      data: {
        event,
      },
    });
  };
}

export function componentScrolled(event) {
  return dispatch => {
    dispatch({
      type: UseCaseActions.COMPONENT_SCROLLED,
      data: {
        event,
      },
    });
  };
}

export function logApiErrorShown({ messageName }) {
  return {
    type: UseCaseActions.LOG_API_ERROR_SHOWN,
    messageName,
  };
}

export function loginListener({ username, password, rememberMe, isUserInitiated, captchaState }) {
  return {
    type: UseCaseActions.LOGIN_LISTENER,
    data: {
      username,
      password,
      rememberMe,
      isUserInitiated,
      ...(!isUndefined(captchaState) && { captchaState }),
    },
  };
}

export function navigateToCommunity(openWindow = false, eventData) {
  return {
    type: UseCaseActions.NAVIGATE_TO_COMMUNITY,
    data: eventData ? { openWindow, eventData } : { openWindow },
  };
}

export function openErrorToast(params) {
  return {
    type: UseCaseActions.OPEN_ERROR_TOAST,
    params,
  };
}

export const openSearch = ({
  searchTerm,
  searchType = 'mainSearch',
  searchFilter = 'all',
  routeEntry = false,
}) => ({
  type: UseCaseActions.OPEN_SEARCH,
  data: {
    searchTerm,
    searchType,
    searchFilter,
    routeEntry,
  },
});

export function pageRendered(event, eventName) {
  return dispatch => {
    dispatch({
      type: UseCaseActions.PAGE_RENDERED,
      data: {
        event,
        eventName,
      },
    });
  };
}

export function pauseTrack({
  track,
  callPlaybackPaused = true,
  logPlaybackActionsAnalytics = true,
  isSimStreamViolation = false,
  isExplicit = true,
} = {}) {
  return {
    type: UseCaseActions.PAUSE_TRACK,
    data: {
      track,
      callPlaybackPaused,
      logPlaybackActionsAnalytics,
      isSimStreamViolation,
      isExplicit,
    },
  };
}

// This is a wrapper function that handles playing/resuming sources within the same
// session for a user. It either calls playSource to start new sources from
// the beginning or intentPlayTrack to resume paused sources from the correct timestamp,
// based on the state of what is currently playing.
// forceRestartSourceIfPlaying is used when tracks are playing amongst their parent sources and
// we want to make sure we start a song source rather than resume the song within the parent source
export function playResumeSource({
  id,
  pandoraType = null,
  forceRestartSourceIfPlaying = false,
  context,
  shouldShuffle = false,
  index,
  sortOrder = null,
  trackToken = null,
  dontIntercept = false,
  pandoraId,
  playSourceUsingPandoraId,
  pageUrl = '',
  stationId = '',
}) {
  return {
    type: UseCaseActions.PLAY_RESUME_SOURCE,
    data: {
      id,
      pandoraType,
      forceRestartSourceIfPlaying,
      context,
      shouldShuffle,
      index,
      sortOrder,
      trackToken,
      pandoraId,
      playSourceUsingPandoraId,
      pageUrl,
      stationId,
    },
    dontIntercept,
  };
}

/**
 * action notifying of the intent to provide feedback
 *
 * @param  {string} trackToken - trackToken
 * @param  {string} sourceId - sourceId
 * @param  {boolean} skip - whether or not to skip track
 * @param  {boolean} isPositive - true for thumb up, false for thumb down
 * @returns {object} - return an Action of type ADD_TRACK_FEEDBACK
 */
export function addTrackFeedback({ trackToken, sourceId, skip = false, isPositive }) {
  return {
    type: Constants.UseCases.ActionTypes.ADD_TRACK_FEEDBACK,
    data: {
      trackToken,
      sourceId,
      skip,
      isPositive,
    },
  };
}

/**
 * action notifying of the intent to removing feedback
 *
 * @param  {string} trackToken - trackToken
 * @param  {string} sourceId - sourceId
 * @param  {boolean} isPositive - true for thumb up, false for thumb down
 * @returns {object} - return an Action of type REMOVE_TRACK_FEEDBACK
 */
export function removeTrackFeedback({ trackToken, sourceId, isPositive }) {
  return {
    type: Constants.UseCases.ActionTypes.REMOVE_TRACK_FEEDBACK,
    data: {
      trackToken,
      sourceId,
      isPositive,
    },
  };
}

export function replayTrack({ sourceId, trackToken, eventSource }) {
  return {
    type: UseCaseActions.REPLAY_TRACK,
    data: {
      sourceId,
      trackToken,
      eventSource,
    },
  };
}

export function resetPasswordAndReauth({
  resetToken,
  newPassword,
  router,
  nextPath,
  username,
  captchaState,
}) {
  return {
    type: UseCaseActions.RESET_PASSWORD_AND_REAUTH,
    data: {
      resetToken,
      newPassword,
      router,
      nextPath,
      username,
      ...(!isUndefined(captchaState) && { captchaState }),
    },
  };
}

export function scrubTrack({ value, relativeValue, callback = noop }) {
  return {
    type: UseCaseActions.SCRUB_TRACK,
    data: {
      value,
      relativeValue,
      callback,
    },
  };
}

export function setSourceRepeatMode({ sourceId, mode }) {
  return {
    type: UseCaseActions.SET_SOURCE_REPEAT_MODE,
    data: {
      sourceId,
      mode,
    },
  };
}

// this function regards in-source-shuffle, different from previous global-shuffle
export function setSourceShuffleMode({ sourceId, enabled }) {
  return {
    type: UseCaseActions.SET_SOURCE_SHUFFLE_MODE,
    data: {
      sourceId,
      enabled,
    },
  };
}

export function signOut(postLogoutPath) {
  return {
    type: UseCaseActions.SIGN_OUT,
    data: {
      postLogoutPath,
    },
  };
}

export function signUp({
  username,
  password,
  birthDay = 1,
  birthMonth = 1,
  birthYear,
  zipcode,
  gender,
  rememberMe,
}) {
  return {
    type: UseCaseActions.SIGN_UP,
    data: {
      username,
      password,
      birthDay: birthDay || 1,
      birthMonth: birthMonth || 1,
      birthYear,
      zipcode,
      gender,
      rememberMe,
    },
  };
}

export function skipTrackBack({ sourceId, index }) {
  return {
    type: UseCaseActions.SKIP_TRACK_BACK,
    data: {
      sourceId,
      index,
    },
  };
}

export function skipTrackForward({
  sourceId,
  parentAction,
  forceNewPlaylist = false,
  skipType = Constants.Playback.FragmentRequestReasons.SKIP,
  type,
  index,
  modePandoraId,
}) {
  return {
    type: UseCaseActions.SKIP_TRACK_FORWARD,
    data: {
      sourceId,
      forceNewPlaylist,
      skipType,
      type,
      parentAction,
      index,
      modePandoraId,
    },
  };
}

export function skipTrackForwardCancel() {
  return {
    type: UseCaseActions.SKIP_TRACK_FORWARD_CANCEL,
  };
}

export function skipAudioAd({
  sourceId,
  skipType = Constants.Playback.FragmentRequestReasons.SKIP,
}) {
  return {
    type: UseCaseActions.SKIP_AUDIO_AD,
    data: { sourceId, skipType },
  };
}

export function skipAd(track, reason = Constants.Playback.FragmentRequestReasons.SKIP) {
  return {
    type: UseCaseActions.SKIP_AD,
    data: { track, reason },
  };
}

/**
 * action notifying of the intent to start a station. Accepts parameters to create the station if the
 * current user doesn't currently have the station
 *
 * @param {string} stationId - the station to play. Only if we are starting a station from a known station
 * @param {string} pandoraId - if it's a new station, use this
 * @param {string} errorRecoveryPath - reroute to this URL on failure
 * @param {boolean} forceReroute - whether to force a URL reroute to nowplaying on this station select
 * @param {boolean} forceNextTrack - whether to force the next track to play or just continue the previously playing track
 * @param {boolean} forceApi - whether to force setStationPlaylist to clear the current playlist tracks and fetch new ones
 * @param {boolean} rerouteBasedOnTier - whether to do a URL reroute to nowplaying on this station select based on the listener's tier
 * @param {boolean} dontIntercept - whether to block the interception of this action
 * @param {boolean} initialPlayback - whether or not this is the first playback of the listening session
 * @param {string} componentName - the name of the component, used by Stats middleware
 * @param {number} stationPosition - the position in the listener's station list
 * @param {number} stationListLength - length of stations in the station list or search results that user had available
 * @param {string} startingTrackToken - the specific track to start playback from
 * @param {boolean} shouldShuffle - if true will start a shuffle station and retrieve the shuffle station id if necessary
 * @param {string} pageUrl - populated when starting a station from a backstage page for branch deep linking purposes
 * @param {object} createStationParams - additional parameters used to create a new station { stationCode, source, musicId }
 *
 * example usage for when startStation is being used for a station the user already has in her stations:
 * startStation({stationId})
 *
 * example usage for when startStation is being used for a station the user may not already have the station:
 * in this case stationCode is used instead of stationId. StationCode needs to be something like
 * StationConstants.MUSIC_ID + musicId (musicId being the Id
 * of whatever we want to start a station from)
 * startStation({stationCode})
 * @returns {object} - return an Action of type START_STATION
 */
export function startStation({
  stationId = null,
  pandoraId = null,
  rerouteBasedOnTier = false,
  errorRecoveryPath = null,
  forceReroute = false,
  forceNextTrack = false,
  forceApi = false,
  dontIntercept = false,
  initialPlayback = false,
  componentName = null,
  stationPosition = null,
  stationListLength = null,
  startingTrackToken = null,
  shouldShuffle = false,
  pageUrl = '',
  ...createStationParams
}) {
  return {
    type: UseCaseActions.START_STATION,
    data: {
      stationId,
      pandoraId,
      rerouteBasedOnTier,
      errorRecoveryPath,
      forceReroute,
      forceNextTrack,
      forceApi,
      componentName,
      stationPosition,
      stationListLength,
      createStationParams,
      startingTrackToken,
      shouldShuffle,
      pageUrl,
    },
    dontIntercept,
    initialPlayback,
  };
}

export function upgrade({ tierSlug, subTierSlug }) {
  return {
    type: Constants.UseCases.ActionTypes.UPGRADE,
    data: {
      tierSlug,
      subTierSlug,
    },
  };
}

// UNUSED
export function webViewModeInteraction(eventData) {
  return dispatch => {
    dispatch({
      type: UseCaseActions.LOG_WEB_VIEW_MODE_EVENT,
      data: eventData,
    });
  };
}

export function generateDeepLink({ sourcePath, pandoraId, sourceType, sourceLocation }) {
  return {
    type: Constants.UseCases.ActionTypes.GENERATE_DEEP_LINK,
    data: {
      sourcePath,
      pandoraId,
      sourceType,
      sourceLocation,
    },
  };
}
